import { ITabObject } from 'customTypes';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { v1 } from 'uuid';
import { IPageInfo, pageInfoState, asideState, asideSubState } from '../atoms';
import { toast } from 'react-toastify';
import Portal from './Portal';
import UserInfoSettingModal from './Modal/UserInfoSettingModal';
import { isMobile } from '../utils/checkMobile';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface styleProps {
  W: number;
}

const Root = styled.div<styleProps>`
  display: flex;
  /* align-items: center; */
  align-items: center;
  align-items: flex-end;
  justify-content: space-between;
  .breadscrum {
    display: flex;
    align-items: center;
    span {
      line-height: 1.4;
      user-select: none;

      &:last-child {
        margin-right: 0.5rem;
      }
      &:not(.material-symbols-rounded) {
        font-size: 0.875rem;
        color: var(--text-tertiary);
        font-weight: 500;
      }
      &.material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20;
        color: var(--outline-em);
      }
    }
  }
  .tab-scroller {
    position: relative;
    width: 86%;
    padding: 0 0.5rem;

    @media screen and (max-width: 1024px) {
      width: 100% !important;
      padding: 0 !important;
    }
  }
  .tab-scroller.with-arrows {
    width: 86%;
    padding: 0 3rem;
    margin: 0 0.5rem;
  }
  .tab-scroller .scroll-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    display: none;
    border: none;
    color: var(--text-tertiary);
    background-color: var(--tonal);
    cursor: pointer;
    height: 2.5rem;
    width: 2.5rem;
    padding: 0 0.5rem;
  }
  .tab-scroller .scroll-arrow:hover {
    background-color: var(--tonal-blue);
    > span {
      color: var(--selected-primary);
    }
  }
  .tab-scroller .scroll-arrow.left {
    left: 0.125rem;
  }
  .tab-scroller .scroll-arrow.right {
    right: 0.125rem;
  }
  @media (min-width: 1025px) {
    .tab-scroller .scroll-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .tab-list {
    display: flex;
    gap: 0;
    align-items: center;
    border-radius: 0.25rem;
    gap: 0.5rem;
    flex-grow: 1;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }
    @media (min-width: 1025px) {
      .with-arrows & {
        width: calc(100% - 6rem);
        margin: 0 auto;
      }
    }
    > a,
    > div {
      border-radius: 0.25rem;
      overflow: hidden;
      padding: 0 1rem;
      min-height: fit-content;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-weight: var(--font-medium);
      color: var(--text-tertiary);
      user-select: none;
      background-color: var(--tonal);
      line-height: 1.2;
      flex: 0 0 auto;
      :hover {
        background-color: var(--tonal-deep);
      }
    }
    div.seperator {
      &:nth-child(2) {
        display: none;
      }
      &:nth-child(n + 3) {
        margin: 0 0.125rem;
        padding: 0;
        width: 2px;
        height: 1.5rem;
        background-color: var(--outline);
      }
    }
    .icon-menu {
      width: 3rem;
      height: 2.5rem;
      border-radius: 0.25rem;
      padding: 0;
      flex-shrink: 0;
      @media (min-width: 1025px) {
        display: none;
      }
    }
    .icon-btn {
      width: 3rem;
      height: 2.5rem;
      border-radius: 0.25rem;
      padding: 0;
      flex-shrink: 0;

      > span {
        font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
      }
    }
    > a.active {
      font-weight: var(--font-semibold);
      background-color: var(--tonal-blue);
      color: var(--tonal);
      color: var(--selected-primary);
      :hover {
      }
    }
    .tab-select-wrapper {
      position: relative;
      flex-grow: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      padding: 0;

      select {
        width: 100%;
        height: 100%;
        flex-grow: 1;
        background-color: var(--tonal);
        padding-left: 1rem;
        padding-right: 2.75rem;
        font-weight: var(--font-medium);
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;

        &:active {
          background-color: var(--tonal-deep);
          outline: none;
        }
      }

      .tab-select-arrow {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 2.5rem;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        color: var(--text-secondary);

        > span {
          font-size: 1.75rem;
          font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        }
      }
    }
  }
`;

interface IProps {
  innerSize: {
    W: number;
    H: number;
  };
  clientSize: {
    W: number;
    H: number;
  };
}

interface ITabProps {
  tabList: ITabObject[];
  currentTabMrCd: string;
  setCurrentTabMrCd: Dispatch<SetStateAction<string>>;
  onClickTab: (key: string) => void;
  size: IProps;
}

interface TabScrollerProps {
  showArrows: boolean;
  onScrollLeft: () => void;
  onScrollRight: () => void;
  listRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

const TabScroller = ({ showArrows, onScrollLeft, onScrollRight, listRef, children }: TabScrollerProps) => {
  return (
    <div className={`tab-scroller ${showArrows ? 'with-arrows' : ''}`}>
      {showArrows && (
        <button type='button' className='icon-btn scroll-arrow left' onClick={onScrollLeft}>
          <span className='material-symbols-rounded'>chevron_left</span>
        </button>
      )}
      <div className='tab-list' ref={listRef}>
        {children}
      </div>
      {showArrows && (
        <button type='button' className='icon-btn scroll-arrow right' onClick={onScrollRight}>
          <span className='material-symbols-rounded'>chevron_right</span>
        </button>
      )}
    </div>
  );
};

const Tab = ({ tabList, currentTabMrCd, setCurrentTabMrCd, onClickTab, size }: ITabProps) => {
  const { t } = useTranslation();
  const [pageInfo, setPageInfo] = useRecoilState<IPageInfo>(pageInfoState);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: 'userSetting', title: t('사용자 정보') });
  const setOpenAside = useSetRecoilState(asideState);
  const setOpenSubAside = useSetRecoilState(asideSubState);
  const tabListRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // 현재 경로에 tabCd 파라미터를 추가한 URL 생성
  const getTabUrl = (mrCd: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tabCd', mrCd);
    return `${location.pathname}?${searchParams.toString()}`;
  };

  const onChangeSelectOption = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    setCurrentTabMrCd(value);
    setPageInfo((prev) => ({ ...prev, mrCd: value })); // 보고서를 선택할 때 mrCd 전역저장
    // Link처럼 URL 업데이트하여 히스토리에 추가
    navigate(getTabUrl(value), { replace: false });
  };

  const onClickReport = (mrCd: string) => {
    onClickTab(mrCd);
    setPageInfo((prev) => ({ ...prev, mrCd })); // 보고서를 선택할 때 mrCd 전역저장
    setOpenSubAside(false); // 탭을 클릭했을 때 서브사이드메뉴 닫기
  };

  // 사용자설정 클릭
  const onClickOpenUserSetting = () => {
    setOpenModal((prev: any) => ({ ...prev, status: true, type: 'userSetting' }));
  };

  const onClickClose = () => {
    setOpenModal((prev: any) => ({ ...prev, status: false, type: 'userSetting' }));
    // if (isMobile()) closeAside();
  };

  useEffect(() => {
    // 페이지 리프레시 할때 mrCd 디폴트값으로 변경
    setPageInfo((prev) => ({ ...prev, mrCd: pageInfo.defaultMrCd }));
  }, []);

  // 탭 스크롤 바 보이기 여부 업데이트 (모바일에서는 항상 비활성)
  const updateArrowsVisibility = useCallback(() => {
    if (isMobile()) {
      setShowArrows(false);
      return;
    }
    const container = tabListRef.current;
    if (!container) return;
    const isOverflowing = container.scrollWidth > container.clientWidth + 1;
    setShowArrows(isOverflowing);
  }, []);

  // 탭 데이터/선택 변경 시에도 가시성 재평가
  useEffect(() => {
    updateArrowsVisibility();
  }, [tabList, currentTabMrCd, updateArrowsVisibility]);

  // 탭 스크롤 바 보이기 여부 업데이트 (리사이즈 시)
  useEffect(() => {
    if (isMobile()) {
      setShowArrows(false);
      return () => {};
    }
    const onResize = () => updateArrowsVisibility();
    window.addEventListener('resize', onResize);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateArrowsVisibility) : null;
    if (ro && tabListRef.current) ro.observe(tabListRef.current);
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro && tabListRef.current) ro.unobserve(tabListRef.current);
    };
  }, [updateArrowsVisibility, size.innerSize.W]);

  const scrollTabs = (direction: 'left' | 'right') => {
    if (isMobile()) return;
    const container = tabListRef.current;
    if (!container) return;
    const scrollAmount = Math.round(container.clientWidth * 0.6);
    const delta = direction === 'left' ? -scrollAmount : scrollAmount;
    container.scrollBy({ left: delta, behavior: 'smooth' });
    // 스크롤 후 상태 업데이트 (부드러운 스크롤 후에도 보정)
    setTimeout(updateArrowsVisibility, 250);
  };

  return (
    <Root W={size.innerSize.W}>
      <TabScroller showArrows={showArrows} onScrollLeft={() => scrollTabs('left')} onScrollRight={() => scrollTabs('right')} listRef={tabListRef}>
        <div
          className='icon-menu hamburger'
          role='presentation'
          onMouseDown={() => {
            if (size.innerSize.W <= 1024) setOpenAside(true);
          }}
        >
          <span className='material-symbols-rounded'>menu </span>
        </div>
        {size.innerSize.W <= 1024 ? (
          <>
            <div className='tab-select-wrapper'>
              <select id='tab-select' onChange={onChangeSelectOption} value={currentTabMrCd}>
                {tabList.map((item) => (
                  <option key={v1()} value={item.mrCd}>
                    {t(item.mrName)}
                  </option>
                ))}
              </select>
              <div className='tab-select-arrow' aria-hidden='true'>
                <span className='material-symbols-rounded'>keyboard_arrow_down</span>
              </div>
            </div>
            <div className='icon-btn' role='presentation' onClick={onClickOpenUserSetting}>
              <span className='material-symbols-rounded'>account_box</span>
            </div>
          </>
        ) : (
          <>
            {tabList.map((item) => (
              <React.Fragment key={v1()}>
                <div className='seperator' />
                <Link key={v1()} to={getTabUrl(item.mrCd)} className={`tab-item ${item.mrCd === currentTabMrCd ? 'active' : ''}`} onClick={() => onClickReport(item.mrCd)}>
                  {t(item.mrName)}
                </Link>
              </React.Fragment>
            ))}
          </>
        )}
      </TabScroller>
      {size.innerSize.W >= 1280 ? (
        <div className='breadscrum'>
          <span className='s-color'>{t(pageInfo?.header.mtName)}</span>
          {pageInfo.header.mdName && (
            <>
              <span className='material-symbols-rounded'>chevron_right</span>
              <span className='s-color'>{t(pageInfo.header.mdName)}</span>
            </>
          )}
        </div>
      ) : (
        ''
      )}

      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'userSetting' && <UserInfoSettingModal openModal={openModal} setOpenModal={setOpenModal} onClickClose={onClickClose} />}
      </Portal>
    </Root>
  );
};

export default Tab;
